import { Json } from "@redotech/json/json";
import { Currency } from "@redotech/money/currencies";
import { ReturnBearCaseMethodType } from "@redotech/returnbear-model/shared/common-enums";
import * as zExt from "@redotech/rpc/ext";

import {
  Address,
  ExternalReturnHubType,
  AddressSchema as TeamAddressSchema,
} from "@redotech/account-common/address";
import { CustomerPortalVersion } from "@redotech/redo-customer-portal-common/customer-portal-version";
import { ReturnStatus } from "@redotech/redo-return-core-common/return-status";
import {
  ApprovalAvailabilityTime,
  AutomationStrategy,
} from "@redotech/redo-shipping-claims-common/automation-strategy";
import { isDefined } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import sumBy from "lodash/sumBy";
import * as z from "zod";
import { AgentReviewMetrics as AgentReviewMetricsSchema } from "./agents/returns-agents/agent-review-metrics";
import { FraudAssessment as FraudAssessmentSchema } from "./agents/returns-agents/fraud-category-output";
import { FraudDetectionInput as FraudDetectionInputSchema } from "./agents/returns-agents/fraud-metrics";
import { ReturnChatSchema } from "./agents/returns-agents/return-chat";
import { ReviewResultSchemaWithThreadId } from "./agents/returns-agents/review-result";
import {
  BLUE_YONDER_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "./dropoff/dropoff-provider";
import {
  BlueYonderQrCodeMetadataSchema,
  VirtualQrCodeMetadataSchema,
} from "./dropoff/dropoff-types";
import {
  ExternalIntegrationIdProviders,
  ExternalIntegrationIdTypes,
} from "./integrations/Integration";
import { InmarIssueCode } from "./integrations/returns/inmar/inmar";
import {
  CreateExternalIntegrationIdProvider,
  CreateExternalIntegrationIdType,
  ExternalRmaIntegration,
} from "./integrations/returns/Integrations";
import { PriceSet, PriceSetSchema } from "./money/price-set";
import { Order, Provider, YofiRiskLevels } from "./order";
import {
  MaybeOriginOrderArray,
  OriginExchangeProductType as OriginExchangeProductTypeEnum,
  OriginOrder,
} from "./origin-order-utils";
import { PaymentIntentItemZodSchema } from "./payment-intent";
import {
  IntegrationProviders,
  ReturnIntegrationErrorCodeZodSchema,
  TrackstarReturnProvider,
} from "./return-integration-types";
import { ReturnMethodZodSchema } from "./return-method";
import { ReturnShipmentErrorCodesZodSchema } from "./return-shipment-types";
import { ChannelId, channelIdToSalesChannel } from "./sales-channels";
import { ShipmentSchema as ShippingProviderShipmentSchema } from "./shipment";
import { ShopifyExchangeOrderStrategy } from "./shopify";
import { TagSchema } from "./tag";
import { ExchangeGroup, ExchangeGroupZodSchema } from "./team";
import { ThirdPartyRefundProvider } from "./third-party-refunds/provider";
import { TimelineEventSchema } from "./timeline";
import { ITrackableZodSchema } from "./trackable-schema";

export { NULL_ORDER_ID } from "./third-party";

export enum ReturnMetadataLockKey {
  BIGCOMMERCE_CREATE_SC_PURCHASE_ORDER = "bigcommerce-create-sc-purchase-order",
  SHOPIFY_CREATE_DRAFT_STORE_CREDIT_ORDER = "shopify-create-draft-store-credit-order",
  RETURN_PROCESSED_EMAIL_SENT = "return-processed-email-sent",
  DISCOUNT_CODE_CREATED = "discount_code_created",
  RISE_CREDIT_ISSUED = "rise_credit_issued",
  LOOPZ_CREDIT_ISSUED = "loopz_credit_issued",
  CREDITSYARD_CREDIT_ISSUED = "creditsyard_credit_issued",
  STORE_CREDIT_EMAILED = "store_credit_emailed",
  GIFT_CARD_CREATED = "gift_card_created",
  SHOPIFY_ACCOUNT_CREDIT_ISSUED = "shopify_account_credit_issued",
  STORE_CREDIT_ORDER_ATTRIBUTES_UPDATED = "store_credit_order_attributes_updated",
  STORE_CREDIT_ORDER_NOTES_UPDATED = "store_credit_order_notes_updated",
  MASKED_CODE = "masked_code",
  BIGCOMMERCE_STORE_CREDIT_ISSUED = "bigcommerce_store_credit_issued",
  BIGCOMMERCE_GIFT_CERTIFICATE_ISSUED = "bigcommerce_gift_certificate_issued",
  MERCHANT_CREDITED_FOR_MANAGED_CLAIM = "merchant-credited-for-managed-claim",
  BIGCOMMERCE_INVENTORY_ADJUSTED_STORE_CREDIT = "bigcommerce-inventory-adjusted-store_credit",
  BIGCOMMERCE_INVENTORY_ADJUSTED_REFUND = "bigcommerce-inventory-adjusted-refund",
  BIGCOMMERCE_INVENTORY_ADJUSTED_EXCHANGE = "bigcommerce-inventory-adjusted-exchange",
  BIGCOMMERCE_INVENTORY_ADJUSTED = "bigcommerce-inventory-adjusted",
  SFCC_EXCHANGE_ORDER_CREATED = "sfcc-exchange-order-created",
  OOS_GIFT_CARD_CREATED = "oos_gift_card_created",
  OOS_DISCOUNT_CODE_CREATED = "oos_discount_code_created",
  OOS_SHOPIFY_ACCOUNT_CREDIT_ISSUED = "oos_shopify_account_credit_issued",
  OOS_STORE_CREDIT_EMAILED = "oos_store_credit_emailed",
  OOS_STORE_CREDIT_ORDER_ATTRIBUTES_UPDATED = "oos_store_credit_order_attributes_updated",
  OOS_STORE_CREDIT_ORDER_NOTES_UPDATED = "oos_store_credit_order_notes_updated",
  DONATION_MATCH_ID = "donation_match_id",
}
export interface Settlement {
  accepted: boolean;
  refund: number;
}

// TODO refactor to remove duplicate definition, some circular dependencies exist currently though preventing this
export enum Payer {
  CUSTOMER = "customer",
  MERCHANT = "merchant",
  REDO = "redo",
}

// FedEx Pickup locations
export enum PickupLocation {
  FRONT_DOOR = "FRONT",
  BACK_DOOR = "REAR",
  SIDE_DOOR = "SIDE",
  OTHER = "NONE",
}

export enum ProvisionType {
  INSTANT = "instant",
  PROCESSED = "processed",
}

export enum AdjustmentType {
  CHOOSE_ITEMS_FLOW = "chooseItemsFlow",
  FINALIZE_FLOW = "finalizeFlow",
  DYNAMIC_BUNDLE_ADJUSTMENT = "dynamicBundleAdjustment",
  MERCHANT_MANUAL = "merchantManual",
  CUSTOMER_LABEL_UPCHARGE_FEE = "customerLabelUpchargeFee",
  USE_IT_OR_LOSE_IT = "useItOrLoseIt",
}
const AdjustmentZodSchema_ = z.object({
  flat: PriceSetSchema,
  proportion: z.number(),
  type: createEnum(AdjustmentType, { enumName: "AdjustmentType" }),
});
export const AdjustmentZodSchema: z.ZodType<AdjustmentWithType> =
  AdjustmentZodSchema_;
export type AdjustmentWithType = z.infer<typeof AdjustmentZodSchema_>;

export const SettlementZodSchema = z.object({
  accepted: z.boolean(),
  refund: z.number(),
});
// UpsellBillingTrigger is similar to Provision Type, but different. It determines when we ACTUALLY charge for the order, regardless of when the order is created (instantly or upon return processed)
export enum UpsellBillingTrigger {
  EXCHANGE_ORDER_CREATED = "exchange_order_created",
  RETURN_CREATED = "return_created",
}

export interface MultipleChoiceTextAnswer {
  answer: string;
  questionText: string;
  step: number;
}

export interface CustomerSummary {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
  shopify_customer_id?: string | null;
}

export interface Fulfillment {
  status: string;
  created?: Date;
}

export const RETURNS_FACET_OPTIONS = [
  "awaiting_disposition",
  ...Object.values(ReturnStatus),
] as const;
export type ReturnsFacetOptions = (typeof RETURNS_FACET_OPTIONS)[number];

export interface ReturnsFacets {
  total: number;
  byReturnFilterFacet: { [T in ReturnsFacetOptions]: number };
}

export interface NeedsActionCounts {
  returns: number;
  claims: number;
}

export const ExchangeItemZodSchema = z.object({
  variantId: z.string(),
  productId: z.string().nullish(),
  sku: z.string().nullish(),
  title: z.string(),
  variantTitle: z.string(),
  images: z.array(z.string().nullable()),
  /** @deprecated use priceSet instead */
  price: z.string(),
  priceSet: PriceSetSchema.nullish(),
  /** @deprecated use itemValueSet instead */
  tax: z.string().nullish(),
  itemValueSet: PriceSetSchema.nullish(),
  /** @deprecated use itemValueSet instead */
  itemValue: z.string().nullish(),
  quantity: z.number(),
  attributes: z
    .array(z.object({ key: z.string(), value: z.string() }))
    .nullish(),
  currencyCode: z.string().nullish(),
});

const MerchantAppExchangeItemZodSchema = ExchangeItemZodSchema.extend({
  outOfStockDuringProcessing: z.boolean().nullish(),
  shopifyVariant: z
    .object({ sku: z.string(), product_id: z.number() })
    .optional(),
});

export type ExchangeItemZod = z.infer<typeof ExchangeItemZodSchema>;
export interface ExchangeItem {
  variantId?: string;
  productId?: string;
  title?: string;
  variantTitle?: string;
  images?: string[];

  /** @deprecated use priceSet instead */
  price?: string;
  priceSet?: PriceSet;
  tax?: string;
  itemValueSet?: PriceSet;
  /** @deprecated use itemValueSet instead */
  itemValue?: string;
  quantity?: number;
  attributes?: { key: string; value: string }[];
  outOfStockDuringProcessing?: boolean;
  sku?: string;
  // FIXME Not contained in the database schema
  currencyCode?: string;
}

export enum ReturnedProductStatus {
  OPEN = "open",
  COMPLETE = "complete",
  // If there is an advanced exchange, we want to wait to process any items until all
  // items have either been processed or rejected, as this will affect all totals.
  // Pending means that the item has been marked as processed but not actually processed
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
  PRE_SHIPMENT = "pre-shipment",
  IN_REVIEW = "in_review",
}

export function canConvertToExchange(
  status: ReturnStatus,
  returnType: CompensationMethod[],
): boolean {
  // Allow exchange conversion for these statuses
  if (returnType.includes(CompensationMethod.EXCHANGE)) {
    return false;
  }

  return [
    ReturnStatus.OPEN,
    ReturnStatus.DELIVERED,
    ReturnStatus.NEEDS_REVIEW,
    ReturnStatus.IN_REVIEW,
    ReturnStatus.IN_TRANSIT,
    ReturnStatus.FLAGGED,
  ].includes(status);
}

export function returnedProductStatusToDisplayText(
  status: ReturnedProductStatus,
): string {
  return {
    [ReturnedProductStatus.OPEN]: "Open",
    [ReturnedProductStatus.COMPLETE]: "Complete",
    [ReturnedProductStatus.PENDING]: "Pending",
    [ReturnedProductStatus.APPROVED]: "Approved",
    [ReturnedProductStatus.REJECTED]: "Rejected",
    [ReturnedProductStatus.PRE_SHIPMENT]: "Pre-shipment",
    [ReturnedProductStatus.IN_REVIEW]: "In review",
  }[status];
}

export enum ReturnSource {
  REDO = "redo",
  EXTERNAL = "external",
}

// FIXME This overloads the "ReturnType<T>" TS utility type - replace it with ReturnTypeEnum everywhere
export type ReturnType =
  | "return"
  | "claim"
  | "warranty"
  | "recycle"
  | "withdrawal";
export enum ReturnTypeEnum {
  CLAIM = "claim",
  RETURN = "return",
  WARRANTY = "warranty",
  RECYCLE = "recycle",
  WITHDRAWAL = "withdrawal",
}

export enum EditableReturnProductType {
  STORE_CREDIT = "store_credit",
  REFUND = "refund",
  EXCHANGE = "exchange",
  EXCHANGE_STORE_CREDIT = "exchange_store_credit",
  EXCHANGE_REFUND = "exchange_refund",
}

export const returnTypeToTitle: Record<ReturnTypeEnum, string> = {
  [ReturnTypeEnum.RETURN]: "Return",
  [ReturnTypeEnum.CLAIM]: "Shipping claim",
  [ReturnTypeEnum.WARRANTY]: "Warranty claim",
  [ReturnTypeEnum.RECYCLE]: "Recycling request",
  [ReturnTypeEnum.WITHDRAWAL]: "Withdrawal",
};

export const RETURN_TYPE_ROUTE: Record<ReturnTypeEnum, string> = {
  [ReturnTypeEnum.RETURN]: "returns",
  [ReturnTypeEnum.CLAIM]: "claims",
  [ReturnTypeEnum.WARRANTY]: "warranties",
  [ReturnTypeEnum.RECYCLE]: "recycle",
  [ReturnTypeEnum.WITHDRAWAL]: "withdrawals",
};

export const RETURN_TYPE_ROUTE_FOR_INDIVIDUAL_RECORD: Record<
  ReturnTypeEnum,
  string
> = {
  [ReturnTypeEnum.RETURN]: "returns/all-returns",
  [ReturnTypeEnum.CLAIM]: "claims/all",
  [ReturnTypeEnum.WARRANTY]: "warranties/all-warranties",
  [ReturnTypeEnum.RECYCLE]: "recycle/all-recycle",
  [ReturnTypeEnum.WITHDRAWAL]: "withdrawals/all-withdrawals",
};

// FIXME This is slightly different than the compensation method enum for draft returns
export enum CompensationMethod {
  REFUND = "refund",
  STORE_CREDIT = "store_credit",
  EXCHANGE = "exchange",
}

export enum InstantRefundStatus {
  PENDING = "pending",
  PAYMENT_FAILED = "payment_failed",
  PAID = "paid",
  PROCESSED = "processed",
  EXPIRED = "expired",
}

export enum GiftCardSource {
  CLIENT = "client",
  EXCHANGE = "exchange",
  EXCHANGE_STOCK = "exchange_stock",
}
export enum StoredCreditSource {
  CLIENT = "client",
  EXCHANGE = "exchange",
  EXCHANGE_STOCK = "exchange_stock",
}

export enum AutomationResult {
  APPROVE = "approve",
  REJECT = "reject",
  MANUAL_REVIEW = "manual_review",
}

export enum ReviewingParty {
  REDO = "redo",
  MERCHANT = "merchant",
}

export enum ClaimResult {
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  PENDING = "PENDING",
}

export enum FileShippingClaimWithSSResult {
  SUCCESS = "success",
  CLAIM_ALREADY_EXISTS = "claim_already_exists",
  FAILED_TO_FILE = "failed_to_file",
}

export enum ExternalClaimStatus {
  NEW = "new",
  CLOSED = "closed",
  UNDER_REVIEW = "under_review",
  PENDING_DOCS = "pending_docs",
  PAID = "paid",
  CANCELLED = "cancelled",
  EXPIRED = "expired",
  PENDING_PREMIUMS = "pending_premiums",
}

export function ReturnAddressToAddress(address: ReturnAddress): Address {
  return {
    name: address.name ?? "",
    street1: address.address1,
    street2: address.address2 ?? "",
    state: address.province ?? "",
    city: address.city,
    country: address.country ?? address.country_code ?? "",
    zip: address.zip ?? "",
    phone: address.phone ?? "",
    returnsCenterId: address.returnsCenterId,
    blueYonderLocationId: address.blueYonderLocationId,
    externalReturnHub: address.externalReturnHub,
    externalLocationId: address.externalLocationId,
  };
}

export const ReturnAddressSchema = z.object({
  name: z.string().nullish(),
  address1: z.string(),
  address2: z.string().nullish(),
  city: z.string(),
  country: z.string().nullish(),
  country_code: z.string().nullish(),
  province: z.string().nullish(),
  province_code: z.string().nullish(),
  zip: z.string().nullish(),
  latitude: z.string().nullish(),
  longitude: z.string().nullish(),
  location: zExt.objectId().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  company: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  externalLocationId: z.string().nullish(),
  trackstarWarehouseId: z.string().nullish(),
  inmarLocationId: z.string().nullish(),
  blueYonderLocationId: z.string().nullish(),
  aims360WarehouseId: z.string().nullish(),
  returnsCenterId: z.string().nullish(),
  externalReturnHub: z
    .object({
      provider: z.nativeEnum(ExternalReturnHubType),
      locationId: z.string().nullish(),
    })
    .nullish(),
});

export type ReturnAddressZod = z.infer<typeof ReturnAddressSchema>;
export interface ReturnAddress {
  name?: string | null;
  address1: string;
  address2?: string | null;
  city: string;
  country?: string | null;
  country_code?: string | null;
  province?: string | null;
  province_code?: string | null;
  zip?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  location?: string | null; // id of location in Redo for return location
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  externalLocationId?: string | null;
  trackstarWarehouseId?: string | null;
  inmarLocationId?: string | null;
  blueYonderLocationId?: string | null;
  aims360WarehouseId?: string | null;
  returnsCenterId?: string | null;
  externalReturnHub?: {
    provider: ExternalReturnHubType;
    locationId?: string | null;
  } | null;
}

export interface Weight {
  amount: string;
  unit: "oz" | "lbs" | "g";
}
export type labelForm = {
  id: symbol;
  items: Product[];
  weight: Weight;
  returnToAddress: ReturnAddress | undefined;
  labelNote: string;
}[];

export enum ReturnTypes {
  STORE_CREDIT = "store_credit",
  REFUND = "refund",
  EXCHANGE = "exchange",
}

export enum ErrorTag {
  LABEL = "label",
  BOXLESS_RETURN = "boxless-return",
  INSTANT_REFUND = "instant-refund",
  PICKUP = "pickup",
  IN_STORE_RETURN = "in-store-return",
  CLAIM_REPORT = "claim-report",
  REPAIR_INVOICE = "repair-invoice",
}

export enum RejectTypes {
  RETURN_PORTAL = "Return portal",
  MANUAL_REVIEW = "Manual review",
  PRE_SHIPMENT = "Pre-shipment",
  EXPIRED = "Expired",
  POST_SHIPMENT = "Post-shipment",
}

export enum ExchangeInvoiceReminderEmailTrigger {
  AUTOMATED = "AUTOMATED",
  MANUAL = "MANUAL",
}
export const ReturnWarningZodSchema = z.object({
  code: z.string(),
  type: createEnum(Object.values(ErrorTag), { enumName: "ErrorTag" }),
  customerMessage: z.string().optional(),
  systemMessage: z.string().optional(),
  error: z.string().optional(),
  errorCode: z.string().optional(),
});

export interface ReturnWarning {
  code: string;
  type: ErrorTag;
  customerMessage?: string;
  systemMessage?: string;
  error?: string;
  errorCode?: string;
}

export const ShipmentSchema = z.object({
  _shipment: ShippingProviderShipmentSchema,
  _refund: z.any().nullish(),
  _refundError: z.any().nullish(),
  // Changed from required to nullable - legacy data in MongoDB may have null addresses
  toAddress: TeamAddressSchema.nullish(),
  // Changed from required to nullable - legacy data in MongoDB may have null addresses
  fromAddress: TeamAddressSchema.nullish(),
  parcel: z.object({
    length: z.number().nullish(),
    width: z.number().nullish(),
    height: z.number().nullish(),
    weight: z.number(),
  }),
  isReturn: z.boolean(),
  mode: createEnum(["test", "production"], { enumName: "ShipmentMode" }),
  pickup: z.any().nullish(),
  postage_label: z.string().nullish(),
  form_label: z.string().nullish(),
  shipmentGroupID: z.string().nullish(),
  is_merchant_carrier_account: z.boolean().nullish(),
  is_merchant_generated_label: z.boolean().nullish(),
  filed_claim: zExt.objectId().nullish(),
  uspsZone: z.number().nullish(),
  insuranceCost: z.number().nullish(),
  merchantUpcharge: z.string().nullish(),
  marketRate: z.string().nullish(),
  calculatedMarketRate: z
    .object({
      marketRate: z.string().nullish(),
      carrier: z.string().nullish(),
      serviceLevel: z.string().nullish(),
      listedRate: z.string().nullish(),
      defaultDiscountPercent: z.number().nullish(),
      teamDiscountPercent: z.number().nullish(),
      _id: zExt.objectId(),
    })
    .nullish(),
  merchantRateOverride: z.string().nullish(),
  shippingProviderRate: z.string().nullish(),
  shouldRemoveMerchantPaidLabelUpcharge: z.boolean().nullish(),
  allRateOptions: z.array(z.any()).nullish(),
  customs_forms: z
    .object({
      commercial_invoices: z
        .array(z.object({ url: z.string(), created_at: z.date().nullish() }))
        .nullable(),
    })
    .nullish(),
});

export type Shipment = z.infer<typeof ShipmentSchema>;

export interface CommercialInvoiceUrl {
  url: string;
  source: "custom" | "easypost";
}

interface ShipmentWithInvoiceData {
  customs_forms?: { commercial_invoices?: { url: string }[] | null } | null;
  _shipment?: { forms?: { form_type?: string; form_url?: string }[] };
}
/**
 * Gets the commercial invoice URL from either the custom commercial invoice
 * (stored in customs_forms) or the EasyPost-generated form.
 * Prioritizes custom commercial invoice over EasyPost forms.
 */
export function getCommercialInvoiceUrl(
  shipment: ShipmentWithInvoiceData | undefined | null,
): CommercialInvoiceUrl | null {
  if (!shipment) {
    return null;
  }

  // First check for custom commercial invoice (our generated form)
  const customInvoice = shipment.customs_forms?.commercial_invoices?.[0];
  if (customInvoice?.url) {
    return { url: customInvoice.url, source: "custom" };
  }

  // Fall back to EasyPost-generated commercial invoice
  const easypostInvoice = shipment._shipment?.forms?.find(
    (form: { form_type?: string }) => form.form_type === "commercial_invoice",
  );
  if (easypostInvoice?.form_url) {
    return { url: easypostInvoice.form_url, source: "easypost" };
  }

  return null;
}

// If you update this object, update MultipleChoiceAnswerZodSchema as well.
export interface MultipleChoiceAnswer {
  result: string[];
  question: string;
  questionText: string;
  answer: string;
}
export const MultipleChoiceAnswerZodSchema = z.object({
  result: z.array(z.string()).nullish(),
  question: z.string().nullish(),
  questionText: z.string(),
  answer: z.string(),
});

export const InputAnswerZodSchema = z.object({
  questionText: z.string(),
  inputType: z.string(),
  answer: z.string().nullish(),
  image: z.string().nullish(),
});

export type InputAnswerZod = z.infer<typeof InputAnswerZodSchema>;

export const ExchangeForZodSchema = z.object({
  images: z.array(z.string()),
  product_id: z.string(),
  price: z.string(),
  option1: z.string().nullish(),
  option2: z.string().nullish(),
  option3: z.string().nullish(),
  variant_id: z.string(),
  sku: z.string().nullish(),
  variant_title: z.string(),
  product_title: z.string(),
});

export type ExchangeForZod = z.infer<typeof ExchangeForZodSchema>;

// If you update this object, update ExchangeGroupItemZodSchema as well.
export interface ExchangeGroupItem {
  productId: string;
  variantId: string;
  title: string;
  variantTitle: string;
  imageSrc: string;
  price?: string;
}

export const ExchangeGroupItemZodSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
  title: z.string(),
  variantTitle: z.string(),
  imageSrc: z.string().nullish(),
  price: z.string().nullish(),
});

export type ExchangeGroupItemZod = z.infer<typeof ExchangeGroupItemZodSchema>;

export enum ReturnProductStrategy {
  STORE_CREDIT = "store_credit",
  REFUND = "refund",
  NONE = "none",
  REPAIR = "repair",
}

export const ProductZodSchema = z.object({
  _id: zExt.objectId(),
  barcodeUrl: z.string().nullish(),
  exchange_for: ExchangeForZodSchema.nullish(),
  images: z.array(z.string().nullable()),
  variant_exists: z.boolean(),
  strategy: z.string(),
  status: createEnum(ReturnedProductStatus, {
    enumName: "ReturnedProductStatus",
  }),
  quantity: z.number(),
  grams: z.number(),
  requiresShipping: z.boolean().nullish(),
  green_return: z.boolean(),
  damaged: z.boolean(),
  wrong_product: z.boolean(),
  reason: z.string().nullish(),
  order: z.union([z.any(), zExt.objectId()]).optional(),
  product_id: z.string().nullish(),
  sku: z.string().nullish(),
  deliveredDate: z.date().nullish(),
  price: z.string(),
  price_adjustment: z.string().nullish(),
  merchant_adjustment: z.string().nullish(),
  adjustments: z.array(AdjustmentZodSchema).nullish(),
  merchant_grade: z.string().nullish(),
  merchant_outcome: z.string().nullish(),
  merchant_notes: z.string().nullish(),
  merchant_submission_notes: z.string().nullish(),
  merchant_images: z.array(z.string()).nullish(),
  item_disposition_audit_log: z
    .array(
      z.object({
        userId: zExt.objectId(),
        timestamp: z.date(),
        updates: z.object({
          merchant_grade: z.string().nullish(),
          merchant_outcome: z.string().nullish(),
          merchant_notes: z.string().nullish(),
        }),
      }),
    )
    .nullish(),
  hasBeenRestockedOnPlatform: z.boolean().nullish(),
  option1: z.string().nullish(),
  option2: z.string().nullish(),
  option3: z.string().nullish(),
  outOfStockDuringProcessing: z.boolean().nullish(),
  variant_id: z.string().nullish(),
  variant_title: z.string(),
  product_title: z.string(),
  line_item_id: z.string(),
  isTnplContractLineItem: z.boolean().nullish(),
  notes: z.string().nullish(),
  cancelled: z.boolean().nullish(),
  rejectMessage: z.string().nullish(),
  isManualReview: z.boolean().optional(),
  percentOfBundle: z.number().nullish(),
  manualReviewReason: z.string().nullish(),
  isFlagged: z.boolean().optional(),
  flaggedReason: z.string().nullish(),
  originalVariantOptions: z
    .object({
      strategy: z.string(),
      exchange_for: ExchangeForZodSchema.nullish(),
      price_adjustment: z.string().nullish(),
      exchangeGroup: ExchangeGroupZodSchema.nullish(),
      exchangeGroupItem: ExchangeGroupItemZodSchema.nullish(),
    })
    .nullish(),
  multipleChoiceAnswers: z.array(MultipleChoiceAnswerZodSchema).nullish(),
  inputAnswers: z.array(InputAnswerZodSchema).nullish(),
  exchangeGroup: ExchangeGroupZodSchema.nullish(),
  exchangeGroupItem: ExchangeGroupItemZodSchema.nullish(),
  shipmentGroupID: z.string().nullish(),
  shipmentGroupIDs: z.array(z.string()).nullish(),
  /** Shopify return ID that this product is associated with (populated when Shopify return is created) */
  shopifyReturnId: z.number().nullish(),
  hsCode: z.string().nullish(),
  dropshipSupplierName: z.string().nullish(),
  isUnbundled: z.boolean().nullish(),
  isCustomItem: z.boolean().nullish(),
  tax: z.number().nullish(),
  aiReturnReasonsThreadId: zExt.objectId().nullish(),
  aiReturnReasonSummary: z.string().nullish(),
  aiImageAnalysis: z.string().nullish(),
  posInput: z.object({ restockRequested: z.boolean().nullish() }).nullish(),
  pendingProcessingInfo: z
    .object({
      reject: z.boolean().nullish(),
      shouldRestock: z.boolean().nullish(),
      handOffToMerchant: z.boolean().nullish(),
    })
    .nullish(),
  /** Information about the return flow used for this item */
  flowInfo: z
    .object({
      /** The ID of the return flow used for this item */
      id: z.string().nullish(),
      /** The path through the return flow that the customer took */
      path: z.array(z.number()),
      /** The starting step index if the return was reset */
      resetPoint: z.number().nullish(),
    })
    .nullish(),
});

export type ProductZod = z.infer<typeof ProductZodSchema>;

// If you update this object, update ProductZodSchema as well.
// If you can use ProductZodSchema, you should.
export interface Product {
  _id: string;
  barcodeUrl?: string;
  exchange_for?: {
    images: string[];
    product_id: string;
    price: string;
    option1: string | null;
    option2: string | null;
    option3: string | null;
    variant_id: string;
    sku?: string;
    variant_title: string;
    product_title: string;
  };
  images: string[];
  variant_exists: boolean;
  strategy: string;
  status: ReturnedProductStatus;
  quantity: number;
  grams: number;
  requiresShipping?: boolean | null;
  green_return: boolean;
  damaged: boolean;
  wrong_product: boolean;
  id: string;
  reason: string;
  order: Order;
  product_id: string;
  price: string;
  price_adjustment?: string;
  adjustments?: AdjustmentWithType[];
  merchant_adjustment?: string;
  merchant_grade?: string;
  merchant_outcome?: string;
  merchant_notes?: string;
  merchant_submission_notes?: string;
  merchant_images?: string[];
  item_disposition_audit_log?: {
    userId: string;
    timestamp: Date;
    updates: {
      merchant_grade?: string;
      merchant_outcome?: string;
      merchant_notes?: string;
    };
  }[];
  hasBeenRestockedOnPlatform?: boolean;
  sku: string;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  variant_id: string;
  variant_title: string;
  product_title: string;
  line_item_id: string;
  notes: string;
  inputAnswers?: InputAnswerZod[];
  cancelled: boolean;
  rejectMessage?: string;
  isManualReview: boolean;
  percentOfBundle?: number;
  manualReviewReason?: string;
  isFlagged: boolean;
  flaggedReason?: string;
  originalVariantOptions?: {
    strategy: string;
    exchange_for?: {
      images: string[];
      product_id: string;
      price: string;
      option1: string | null;
      option2: string | null;
      option3: string | null;
      variant_id: string;
      sku?: string;
      variant_title: string;
      product_title: string;
    };
    price_adjustment?: string;
    exchangeGroup?: ExchangeGroup;
    exchangeGroupItem?: ExchangeGroupItem;
  };
  multipleChoiceAnswers?: MultipleChoiceAnswer[];
  exchangeGroup?: ExchangeGroup;
  exchangeGroupItem?: ExchangeGroupItem;
  shipmentGroupID?: string;
  isTnplContractLineItem?: boolean;
  exchange_properties: Json;
  // This is for products that have multiple labels. they belong to multiple shipments
  // currently only used fro create shipments modal. otherwise use
  // shipmentGroupID
  shipmentGroupIDs?: string[];
  isCustomItem?: boolean;
  /** Shopify return ID that this product is associated with (populated when Shopify return is created) */
  shopifyReturnId?: number;
  isVariantExchange: () => boolean;
  hsCode?: string;
  dropshipSupplierName?: string;
  outOfStockDuringProcessing?: boolean;
  deliveredDate?: string;
  aiReturnReasonsThreadId?: string | null;
  aiReturnReasonSummary?: string | null;
  pendingProcessingInfo?: {
    reject?: boolean;
    shouldRestock?: boolean;
    handOffToMerchant?: boolean;
  };
  /** Information about the return flow used for this item */
  flowInfo?: {
    /** The ID of the return flow used for this item */
    _id?: string;
    /** The path through the return flow that the customer took */
    path: number[];
    /** The starting step index if the return was reset */
    resetPoint?: number;
  };
}

interface Metadata {
  key: string;
  value: string;
}

export const MetadataZodSchema = z.object({
  key: z.string(),
  value: z.string(),
});

export const CustomerSummaryZodSchema = z.object({
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  email: z.string().nullish(),
  phone_number: z.string().nullish(),
  shopify_customer_id: z.string().nullish(),
});

export const FulfillmentZodSchema = z.object({
  status: z.string(),
  created: z.date().nullish(),
});

export const CreditSourceZodSchema = createEnum(StoredCreditSource, {
  enumName: "CreditSource",
});
export const GiftCardSourceZodSchema = createEnum(GiftCardSource, {
  enumName: "GiftCardSource",
});

export const ShipmentGroupZodSchema = z.object({
  id: z.string(),
  address: ReturnAddressSchema,
  grams: z.number().nullish(),
  labelInstructions: z.string().nullish(),
  donationMatchIds: z.array(z.string()).nullish(),
});
export interface ShipmentGroup {
  id: string;
  address: ReturnAddress;
  grams?: number;
  labelInstructions?: string;
  donationMatchIds?: string[];
}

export const TotalsZodSchema = z.object({
  /** @deprecated Get this value from return totals calculator instead */
  refund: z.number().nullish(),
  /** @deprecated Get this value from return totals calculator instead */
  storeCredit: z.number().nullish(),
  /** @deprecated Get this value from return totals calculator instead */
  greenReturnCredit: z.number().nullish(),
  /** @deprecated Use shippingFee instead */
  fee: z.number(),
  returnCollectionHoldAmount: z.number().nullish(),
  depositRefunded: z.boolean().nullish(),
  /** @deprecated Get this value from return totals calculator instead */
  charge: z.number().nullish(),
  repair: z.number().nullish(),
  customerLabelUpchargeFee: z.number().nullish(),
});
export interface Totals {
  /** @deprecated Get this value from return totals calculator instead */
  refund?: number;
  /** @deprecated Get this value from return totals calculator instead */
  storeCredit?: number;
  /** @deprecated Get this value from return totals calculator instead */
  greenReturnCredit?: number;
  /** @deprecated Use shippingFee instead */
  fee: number;
  returnCollectionHoldAmount?: number;
  depositRefunded?: boolean;
  /** @deprecated Get this value from return totals calculator instead */
  charge?: number;
  repair?: number;
  customerLabelUpchargeFee?: number;
}

export enum Channel {
  SHOP_APP = "shopApp",
  POINT_OF_SALE = "pointOfSale",
  WEB = "web",
  AI = "ai", // AI chat returns, no UI/UX except for chatting to the agent
  API = "api",
}
export const ChannelZod = createEnum(Channel, { enumName: "Channel" });

export const OrderSummaryZodSchema = z.object({
  name: z.string(),
  number: z.union([z.string(), z.number()]).nullish(),
  order: zExt.objectId(),
  shippingZipCode: z.string().nullish(),
});

export interface OrderSummary {
  name: string;
  number?: number;
  order: string;
  shippingZipCode?: string | null;
}

export const NotesZodSchema = z.object({
  notesToCustomer: z.string().nullish(),
});

/**
 * Represents notes related to the return life cycle.
 * Add additional notes if needed as the return process evolves.
 */
export interface Notes {
  /**
   * A note from the merchant intended for the customer.
   * This field can be used to provide information or updates related to the return.
   */
  notesToCustomer?: string;
  toCustomerOnApproval?: string;
  toCustomerOnProcessing?: string;
  imageUrlsOnApproval?: string[];
}

export const DiscountAllocationZodSchema = z.object({
  discountedAmount: z.object({ amount: z.string() }),
});
export interface DiscountAllocation {
  discountedAmount: { amount: string };
}

export const OriginExchangeProductType = createEnum(
  OriginExchangeProductTypeEnum,
  { enumName: "OriginExchangeProductTypeEnum" },
);

const BaseOriginExchangeProductZodSchema = z.object({
  type: OriginExchangeProductType,
  newProductId: z.string(),
  newVariantId: z.string(),
});

export const YofiRiskLevelsZod = createEnum(YofiRiskLevels, {
  enumName: "YofiRiskLevels",
});

export const YofiIndicatorZod = z.object({
  name: z.string().nullish(),
  is_risk: z.boolean().nullish(),
});

export const YofiScoreZodSchema = z.object({
  prediction_name: z.string(),
  prediction_value: YofiRiskLevelsZod,
  justification: z.string().nullish(),
  indicators: z.array(YofiIndicatorZod).nullish(),
});

const OriginVariantExchangeProductZodSchema =
  BaseOriginExchangeProductZodSchema.extend({
    type: z.literal(OriginExchangeProductTypeEnum.VARIANT),
    lineItemId: z.string(),
    previousVariantId: z.string(),
    isUnbundled: z.boolean(),
    price: z.string(),
    tax: z.string(),
  });

const OriginAdvancedExchangeProductZodSchema =
  BaseOriginExchangeProductZodSchema.extend({
    type: z.literal(OriginExchangeProductTypeEnum.ADVANCED),
    discountPrice: z.string(),
    price: z.string(),
    tax: z.string(),
  });

const OriginExchangeProductZodSchema = z.union([
  OriginVariantExchangeProductZodSchema,
  OriginAdvancedExchangeProductZodSchema,
]);

export const OriginLineItemZodSchema = z.object({
  id: z.string(),
  variantId: z.string(),
  discountPrice: z.string(),
  price: z.string(),
  tax: z.string(),
});

export const OriginReturnZodSchema = z.object({
  exchangeProducts: z.array(OriginExchangeProductZodSchema),
  originOrder: z.lazy(() => OriginOrderZodSchema),
  totalAdjustments: z.number(),
});

export const OriginOrderZodSchema: z.ZodType<OriginOrder> = z.object({
  isExchangeOrder: z.boolean(),
  originReturn: OriginReturnZodSchema.optional(),
  lineItems: z.array(OriginLineItemZodSchema),
});

export const MaybeOriginOrderZodSchema = z.union([
  OriginOrderZodSchema,
  z.object({ error: z.string() }),
]);

export const MaybeOriginOrderArrayZodSchema: z.ZodType<MaybeOriginOrderArray> =
  z.tuple([MaybeOriginOrderZodSchema]).rest(MaybeOriginOrderZodSchema);

export const InmarBoxZodSchema = z.object({
  shipmentGroupId: z.string(),
  boxBarcode: z.string(),
  boxId: z.string(),
  added: z.boolean(),
});

const InmarRAZodSchema = z.object({ raKey: z.string(), raId: z.string() });

const InmarReturnZodSchema = z.object({
  returnKey: z.string(),
  returnId: z.string(),
});

const InmarProcessedItemZodSchema_ = z.object({
  itemKey: z.string().nullish(),
  description: z.string(),
  quantity: z.number(),
});
const InmarProcessedItemZodSchema: z.ZodType<InmarProcessedItem> =
  InmarProcessedItemZodSchema_;
export type InmarProcessedItem = z.infer<typeof InmarProcessedItemZodSchema_>;

export const InmarZodSchema_ = z.object({
  ra: InmarRAZodSchema.nullish().describe(
    "If present, we successfully created an RA in Inmar.",
  ),
  return: InmarReturnZodSchema.nullish().describe(
    "If present, we successfully created a return on our RA in Inmar.",
  ),
  boxes: z
    .array(InmarBoxZodSchema)
    .describe(
      "If any present, we successfully added items to the box on our return in Inmar.",
    )
    .nullish(),
  issueCode: createEnum(InmarIssueCode, { enumName: "InmarIssueCode" })
    .nullish()
    .describe("If present, there was an issue with the Inmar integration."),
  issueMessage: z
    .string()
    .nullish()
    .describe("Description of issue, if there was one."),
  processedItems: z
    .array(InmarProcessedItemZodSchema)
    .nullish()
    .describe("Items in the return."),
});
const InmarZodSchema: z.ZodType<InmarZod> = InmarZodSchema_;
type InmarZod = z.infer<typeof InmarZodSchema_>;

export const BlueYonderWebhookEventZodSchema = z.object({
  eventType: z.string(),
  status: z.string(),
  eventDateTime: z.date(),
  eventId: z.string(),
});

export type BlueYonderWebhookEvent = z.infer<
  typeof BlueYonderWebhookEventZodSchema
>;

// BlueYonder schemas for boxless returns with QR codes
export const BlueYonderReturnZodSchema = z.object({
  shipmentGroupId: z.string(),
  refReturnId: z.string(),
  qrCode: z
    .string()
    .describe("QR code for customer to use at dropoff location"),
  qrCodeUri: z
    .string()
    .nullish()
    .describe("URI of a QR code PNG stored in S3 for this BlueYonder return"),
  status: z.string().nullish(),
  labelValue: z
    .string()
    .nullish()
    .describe("Label value from BlueYonder API for webhook matching"),
  webhookEvents: z
    .array(BlueYonderWebhookEventZodSchema)
    .describe("History of webhook events received"),
  lastWebhookUpdate: z.date().nullish(),
  createdAt: z.date(),
});

/**
 * @deprecated Replaced by `dropoffs.qrCodes` on Return. Retained on the schema
 * so the BlueYonder webhook receiver and trackable/email-reminder legacy paths
 * can continue to read/write existing data; do not add new readers.
 */
export const BlueYonderZodSchema_ = z.object({
  returns: z
    .array(BlueYonderReturnZodSchema)
    .describe("BlueYonder returns created for each shipment group"),
  chosen: z
    .boolean()
    .nullish()
    .describe("Whether customer selected BlueYonder as their shipment method"),
  /** Flat rate dropoff fee configured on the Return flow step */
  dropoffFlatRateFee: z.number().nullish(),
});
/** @deprecated See {@link BlueYonderZodSchema_}. */
export const BlueYonderZodSchema: z.ZodType<BlueYonderZod> =
  BlueYonderZodSchema_;
/** @deprecated See {@link BlueYonderZodSchema_}. */
export type BlueYonderZod = z.infer<typeof BlueYonderZodSchema_>;

export const ReturnBearShippingInfoSchema = z.object({
  carrierName: z.string(),
  shippingLabelUrl: z.string(),
  trackingNumber: z.string(),
  trackingUrl: z.string(),
});

const typeEnum: typeof ReturnBearCaseMethodType = ReturnBearCaseMethodType;

const ReturnBearSelectedMethodEnum = createEnum(typeEnum, {
  enumName: "ReturnBearCaseMethodType",
});

export const ReturnBearReturnZodSchema_ = z.object({
  returnBearCaseId: z.string(),
  returnBearCaseShortId: z.string(),
  selectedReturnMethod: ReturnBearSelectedMethodEnum,
  qrCodeUri: z.string().nullish(),
  shippingInfo: ReturnBearShippingInfoSchema.nullish(),
  /** Flat rate label fee configured on the Return flow step (for mail-in via ReturnBear courier) */
  labelFlatRateFee: z.number().nullish(),
  /** Flat rate dropoff fee configured on the Return flow step (for drop-off at ReturnBear location) */
  dropoffFlatRateFee: z.number().nullish(),
});

export const ReturnBearZodSchema = ReturnBearReturnZodSchema_;

export type ReturnBearZod = z.infer<typeof ReturnBearZodSchema>;

const returnDropoffQrCodeBase = {
  shipmentGroupId: z.string(),
  qrCode: z.string().nullish(),
  qrCodeUrl: z.string().nullish(),
  /** Flat rate dropoff fee configured on the Return flow step for this provider */
  dropoffFlatRateFee: z.number().nullish(),
};

const ReturnDropoffQrCodeSchema_ = z.discriminatedUnion("provider", [
  z.object({
    ...returnDropoffQrCodeBase,
    provider: z.literal(VIRTUAL_PROVIDER),
    providerMetadata: VirtualQrCodeMetadataSchema.nullish(),
  }),
  z.object({
    ...returnDropoffQrCodeBase,
    provider: z.literal(BLUE_YONDER_PROVIDER),
    providerMetadata: BlueYonderQrCodeMetadataSchema.nullish(),
  }),
  z.object({
    ...returnDropoffQrCodeBase,
    provider: z.literal(RETURN_BEAR_PROVIDER),
    providerMetadata: z.object({}).nullish(),
  }),
]);
export const ReturnDropoffQrCodeSchema: z.ZodType<ReturnDropoffQrCode> =
  ReturnDropoffQrCodeSchema_;
export type ReturnDropoffQrCode = {} & z.infer<
  typeof ReturnDropoffQrCodeSchema_
>;

const ReturnDropoffSchema_ = z.object({
  chosen: z.boolean().nullish(),
  qrCodes: z.array(ReturnDropoffQrCodeSchema),
});
export const ReturnDropoffSchema: z.ZodType<ReturnDropoff> =
  ReturnDropoffSchema_;
export interface ReturnDropoff extends z.infer<typeof ReturnDropoffSchema_> {}

export const ReshopTriggerZodSchema_ = z.object({
  triggerType: z.string(),
  timestamp: z.date(),
});
export const ReshopTriggerZodSchema: z.ZodType<ReshopTriggerZod> =
  ReshopTriggerZodSchema_;
export type ReshopTriggerZod = z.infer<typeof ReshopTriggerZodSchema_>;

export const ReshopZodSchema_ = z.object({
  shopperClaimed: z.boolean(),
  receivedTriggers: z.array(ReshopTriggerZodSchema),
});
export const ReshopZodSchema: z.ZodType<ReshopZod> = ReshopZodSchema_;
export type ReshopZod = z.infer<typeof ReshopZodSchema_>;

export const ReturnLoyaltyDataZodSchema = z.object({
  enabled: z.boolean(),
  pointsPerDollar: z.number(),
  currentPoints: z.number(),
  loyaltyPointsRedeemedOnOrder: z.number(),
  lineItemPointsEarned: z.record(z.string(), z.number()),
});

const MoneyZodSchema = z.object({
  amount: z.string(),
  currencyCode: z.enum(Currency),
});

const PriceSetZodSchema = z.object({
  shopMoney: MoneyZodSchema,
  presentmentMoney: MoneyZodSchema,
});

const ReturnTrackingTriggerTriggeredSchema_ = z.object({
  trigger: z.string(),
  triggeredAt: z.date(),
});
export const ReturnTrackingTriggerTriggeredSchema: z.ZodType<ReturnTrackingTriggerTriggered> =
  ReturnTrackingTriggerTriggeredSchema_;
export interface ReturnTrackingTriggerTriggered
  extends z.infer<typeof ReturnTrackingTriggerTriggeredSchema_> {}

const ReturnProcessingTriggerSchema_ = z.object({
  type: z.enum(["manual", "automated"]),
  initiator: z.string().nullish(),
  automationReason: z.string().nullish(),
  userId: zExt.objectId().nullish(),
  triggeredAt: z.date(),
  productIds: z.array(zExt.objectId()),
  returnsCenterId: z.string().nullish(),
});
export const ReturnProcessingTriggerSchema: z.ZodType<ReturnProcessingTrigger> =
  ReturnProcessingTriggerSchema_;
export interface ReturnProcessingTrigger
  extends z.infer<typeof ReturnProcessingTriggerSchema_> {}

export enum InvoiceStatus {
  COMPLETE = "complete",
  PENDING = "pending",
}

export const createRmaResultsZodSchema = z.object({
  name: z.union([
    createEnum(IntegrationProviders, { enumName: "IntegrationProviders" }),
    createEnum(TrackstarReturnProvider, {
      enumName: "TrackstarReturnProvider",
    }),
  ]),
  successful: z.boolean().default(false),
  errorCode: ReturnIntegrationErrorCodeZodSchema.nullish(),
  merchantStopRetries: z.boolean().default(false),
  merchantDismissed: z.boolean().default(false),
  errorMessage: z.string().nullish(),
});

export const createRmaResultsArrayZodSchema = z
  .array(createRmaResultsZodSchema)
  .nullish();
export type CreateRmaResults = z.infer<typeof createRmaResultsZodSchema>;
export type CreateRmaResultsArray = z.infer<
  typeof createRmaResultsArrayZodSchema
>;

export const ExternalRmaStatus = ["succeeded", "failed", "dismissed"] as const;
export type ExternalRmaStatus = (typeof ExternalRmaStatus)[number];

export const createExternalRmaResultsZodSchema = z.object({
  provider: createEnum(ExternalRmaIntegration, {
    enumName: "ExternalRmaIntegration",
  }),
  status: createEnum(ExternalRmaStatus, { enumName: "ExternalRmaStatus" }),
  errorCode: ReturnIntegrationErrorCodeZodSchema.nullish(),
  errorMessage: z.string().nullish(),
  attemptedAt: z.date(),
  attemptCount: z.number().int().positive().default(1),
});

export const createExternalRmaResultsArrayZodSchema = z
  .array(createExternalRmaResultsZodSchema)
  .nullish();
export type CreateExternalRmaResults = z.infer<
  typeof createExternalRmaResultsZodSchema
>;
export type CreateExternalRmaResultsArray = z.infer<
  typeof createExternalRmaResultsArrayZodSchema
>;

export const createShipmentFailureZodSchema = z.object({
  errorCode: ReturnShipmentErrorCodesZodSchema.nullable(),
  dismissed: z.boolean().default(false),
  shipmentIds: z.array(z.string()).nullable(),
});

export const createShipmentFailuresArrayZodSchema = z
  .array(createShipmentFailureZodSchema)
  .nullish();
export type CreateShipmentFailure = z.infer<
  typeof createShipmentFailureZodSchema
>;
export type CreateShipmentFailuresArray = z.infer<
  typeof createShipmentFailuresArrayZodSchema
>;

export const ReturnZodSchema_ = ITrackableZodSchema.extend({
  _id: zExt.objectId(),
  createdAt: z.date(),
  updatedAt: z.date(),
  loopId: z.number(),
  externalId: z.string().nullish(),
  idempotencyKey: z.string().nullish(),
  deletedAt: z.date().nullish(),
  referralId: z.string().nullish(),
  metadata: z.array(MetadataZodSchema).optional(),
  tags: z.array(TagSchema).nullish(),
  status: createEnum(ReturnStatus, { enumName: "ReturnStatus" }),
  invalid: z.boolean().nullish(),
  flaggedByYofi: z.boolean().default(false),
  allProductsDispositionCompleted: z.boolean().nullish(),
  trackstarRmaIds: z.array(z.string()).nullish(),
  integrationRmaIds: z.array(z.string()).nullish(),
  customer: CustomerSummaryZodSchema.nullish(),
  fulfillment: FulfillmentZodSchema,
  processed: z
    .object({
      refunds: z.boolean(),
      storeCredit: z.boolean(),
      exchanges: z.boolean(),
    })
    .optional(),
  provision: createEnum(ProvisionType, {
    enumName: "ProvisionType",
  }).optional(),
  refunds: z.array(z.any()),
  storedCredits: z.array(
    z.object({
      createdAt: z.date(),
      updatedAt: z.date(),
      priceRuleId: z.string().nullish(),
      discountCodeId: z.string().nullish(),
      source: CreditSourceZodSchema.nullish(),
      products: z.array(zExt.objectId()).nullish(),
      value: z.number().nullish(),
      greenReturn: z.boolean(),
    }),
  ),
  channel: ChannelZod.optional(),
  giftCards: z.array(
    z.object({
      createdAt: z.date(),
      updatedAt: z.date(),
      giftCardId: z.union([z.string(), z.number().transform(String)]).nullish(),
      code: z.string().nullish(),
      source: GiftCardSourceZodSchema.nullish(),
      products: z.array(z.string()).nullish(),
      value: z.number().nullish(),
      greenReturn: z.boolean(),
    }),
  ),
  products: z.array(ProductZodSchema),
  barcodes: z.array(z.string()).nullish(),
  cancelled_products: z.array(ProductZodSchema).nullish(),
  shipment: ShipmentSchema.optional(),
  shipments: z.array(ShipmentSchema).optional(),
  reShipments: z.array(ShipmentSchema).nullish(),
  deletedShipments: z.array(ShipmentSchema).nullish(),
  nonReturnCustomShipments: z.array(ShipmentSchema).nullish(),
  shipmentGroups: z.array(ShipmentGroupZodSchema).nullish(),
  paymentIntents: z.array(PaymentIntentItemZodSchema).optional(),
  adjustments: z.array(AdjustmentZodSchema).nullish(),
  exchangeOrder: z.array(z.any()).optional(),
  exchangeLineItemIds: z.array(z.string()).nullish(),
  exchangeShippingName: z.string().nullish(),
  exchangeOrderTags: z.array(z.string()).nullish(),
  exchangeShippingFee: z.number().nullish(),
  chargesExchangeShippingViaStripe: z.boolean().nullish(),
  discountCodeName: z
    .object({ prefix: z.string().nullish(), suffix: z.string().nullish() })
    .nullish(),
  warrantyExchangeDiscountTitle: z.string().nullish(),
  draftOrderId: z.number().nullish(),
  draftOrderURL: z.string().optional(),
  markedForManualReview: z.boolean().optional(),
  reviewingParty: createEnum(ReviewingParty, {
    enumName: "ReviewingParty",
  }).nullish(),
  automationStrategy: z
    .object({
      strategy: createEnum(AutomationStrategy, {
        enumName: "AutomationStrategy",
      }),
      options: z
        .object({
          timeTillApprovalMs: z.number().nullish(),
          configuredApprovalDurationMs: z.number().nullish(),
          approvalAvailabilityTime: createEnum(ApprovalAvailabilityTime, {
            enumName: "ApprovalAvailabilityTime",
          }).nullish(),
        })
        .nullish(),
    })
    .nullish(),
  automationWorkflowIds: z.array(z.string()).nullish(),
  automationResult: z.enum(AutomationResult).nullish(),
  postage_label: z.string().nullish(),
  packingSlipLink: z.string().nullish(),
  form_label: z.string().nullish(),
  totals: TotalsZodSchema,
  shippingFee: PriceSetZodSchema.optional(),
  isFlatShipping: z.boolean().nullish(), // TODO: required
  isLabelUpcharge: z.boolean().nullish(),
  redistributeBogoDiscounts: z.boolean().nullish(),
  returnMethod: ReturnMethodZodSchema.nullish(),
  greenReturn: z
    .object({
      discount: z
        .object({
          amount: z.number(),
          discountCodeId: z.string().nullish(),
          priceRuleId: z.number().nullish(),
          code: z.string().nullish(),
        })
        .nullish(),
      refund: z.object({ amount: z.number().nullish() }).nullish(),
    })
    .nullish(),
  stripe: z
    .object({
      payment_token: z.string().nullish(),
      customer_id: z.string().nullish(),
    })
    .nullish(),
  instantExchangeRecovery: z
    .object({
      stripeInfo: z.object({
        customer_id: z.string().nullish(),
        payment_token: z.string().nullish(),
        setup_intent: zExt.objectId().nullish(),
      }),
      attempts: z.array(
        z.object({ createdAt: z.date(), successful: z.boolean() }),
      ),
    })
    .nullish(),
  labelDeductedFromCredit: z.boolean().nullish(),
  labelDeductedFromExchange: z.boolean().nullish(),
  shippingFeeToDeduct: z.number().nullish(),
  merchant_waived_shipping_fee_deduction: z.boolean().nullish(), // TODO: required
  merchant_waived_shipping_fee_amount: z.number().nullish(),
  newOrderPriceAdjustment: z
    .object({ type: z.enum(["amount", "percentage"]), value: z.number() })
    .optional(),
  timeline: z.array(TimelineEventSchema),
  labelPayer: z.enum(Payer).nullish(),
  team: zExt.objectId(),
  options: z
    .object({
      sendNotifications: z.boolean(),
      waiveDraftFee: z.boolean().optional(),
    })
    .optional(),
  address: ReturnAddressSchema.nullish(),
  shipping_address: ReturnAddressSchema.nullish(),
  newOrderAddress: ReturnAddressSchema.nullish(),
  merchant_address: ReturnAddressSchema.nullish(),
  customerTimeZone: z.string().nullish(),
  freeNewOrderShipping: z.boolean().optional(),
  damaged: z.boolean(),
  wrongProduct: z.boolean(),
  shopifyExchangeOrderStrategy: z.enum(ShopifyExchangeOrderStrategy).nullish(),
  shopifyReturnId: z.number().nullish(),
  shopifyReturnIds: z.array(z.number()).nullish(),
  /**
   * @deprecated 18th-Jun-2025 (Use metadata instead)
   */
  netsuiteReturnAuthorization: z.object({ id: z.number().nullish() }).nullish(),
  /**
   * @deprecated 18th-Jun-2025 (Use metadata instead)
   */
  netsuiteItemReceipt: z.object({ id: z.number().nullish() }).nullish(),
  /**
   * @deprecated 18th-Jun-2025 (Use metadata instead)
   */
  netsuiteCreditMemo: z.object({ id: z.number().nullish() }).nullish(),
  notes: NotesZodSchema.nullish(),
  orders: z.array(OrderSummaryZodSchema).default([]),
  type: createEnum(ReturnTypeEnum, { enumName: "ReturnTypeEnum" }),
  returnType: z.array(
    createEnum(CompensationMethod, { enumName: "CompensationMethod" }),
  ),
  analyticsTotals: z
    .object({
      orderName: z.string().nullish(),
      returnValueNoTaxAdjustment: z.number().nullish(),
      returnTax: z.number().nullish(),
      returnAdjustment: z.number().nullish(),
      returnTotalValue: z.number().nullish(),
      returnExchangeValue: z.number().nullish(),
      returnStoreCreditValue: z.number().nullish(),
      returnRefundValue: z.number().nullish(),
      containsGreenReturn: z.boolean().nullish(),
    })
    .nullish(),
  analyticsTotalsV2: z
    .object({
      orderName: z.string().nullish(),
      returnValueNoTaxNoAdjustment: z.number().nullish(),
      returnTax: z.number().nullish(),
      returnTotalAdjustments: z.number().nullish(),
      returnMerchantAdjustment: z.number().nullish(),
      returnPriceAdjustment: z.number().nullish(),
      returnTotalValue: z.number().nullish(),
      returnNewOrderTotalValue: z.number().nullish(),
      returnNewOrderDifferenceValue: z.number().nullish(),
      returnExchangeValue: z.number().nullish(),
      returnStoreCreditValue: z.number().nullish(),
      returnRefundValue: z.number().nullish(),
      returnNonAccruableCreditTotalValue: z.number().nullish(),
      returnNonAccruableCreditLeftoverValue: z.number().nullish(),
      returnNonAccruableCreditUsedValue: z.number().nullish(),
      returnOutstandingBalance: z.number().nullish(),
      returnShippingFee: z.number().nullish(),
      returnShippingFeeDeductedFromRefund: z.number().nullish(),
      returnShippingFeeDeductedFromStoreCredit: z.number().nullish(),
      returnShippingFeeDeducted: z.number().nullish(),
      returnTotalBonuses: z.number().nullish(),
      returnTotalFees: z.number().nullish(),
      containsGreenReturn: z.boolean().nullish(),
    })
    .nullish(),
  operations: z
    .object({
      processedBy: z.string().nullish(),
      processedAt: z.date().nullish(),
      processedEmail: z.string().nullish(),
      approvedBy: z.string().nullish(),
      approvedAt: z.date().nullish(),
      approvedEmail: z.string().nullish(),
      createdBy: z.string().nullish(),
      createdEmail: z.string().nullish(),
    })
    .nullish(),
  rejectType: createEnum(RejectTypes, { enumName: "RejectTypes" }).nullish(),
  originalReturnOptions: z
    .object({
      advancedExchangeItems: z.array(ExchangeItemZodSchema),
      new_order_taxes: z.string().optional(),
      discount_allocations: z.array(DiscountAllocationZodSchema).optional(),
    })
    .nullish(),
  processAt: z
    .object({
      exchange: z.date().nullish(),
      storeCredit: z.date().nullish(),
      refund: z.date().nullish(),
    })
    .nullish(),
  sentInvoice: z.boolean().optional(),
  sentInvoiceReminders: z.array(z.object({ sentAt: z.date() })).optional(),
  completedAt: z.date().nullish(),
  closedWithNoAction: z.boolean().nullish(),
  wasReopened: z.boolean().default(false),
  pickup: z
    .looseObject({
      email: z.string().nullish(),
      phone: z.string().nullish(),
      pickupDate: z.string(),
      pickupLocation: z.looseObject({
        packageLocation: createEnum(PickupLocation, {
          enumName: "PickupLocation",
        }),
        specialInstructions: z.string().nullish(),
      }),
      surveySent: z.boolean().nullish(),
      pickupPayer: z.string().nullish(),
    })
    .nullish(),
  pickupMetrics: z
    .object({
      eligible: z.boolean().nullish(),
      normalLabel: z.number().nullish(),
      rate: z.number().nullish(),
      coveredByRedo: z.boolean().nullish(),
      isMobile: z.boolean().nullish(),
      areaType: z.enum(["standard", "pickup", "extended", "remote"]).nullish(),
      isSameDay: z.boolean().nullish(),
      isWeekend: z.boolean().nullish(),
      estimatedCost: z.number().nullish(),
      estimatedMargin: z.number().nullish(),
    })
    .nullish(),
  pickupCancelation: z
    .object({
      pickupDetails: z
        .object({
          email: z.string().nullish(),
          phone: z.string().nullish(),
          pickupDate: z.string(),
          pickupLocation: z.object({
            packageLocation: z.string(),
            specialInstructions: z.string().nullish(),
          }),
          surveySent: z.boolean().nullish(),
          pickupPayer: z.string().nullish(),
          textReminder: z.boolean().nullish(),
          status: z.string().nullish(),
        })
        .nullish(),
      pickupShippingDetails: z.any().nullish(),
      refundAmount: z.number().nullish(),
      wasRefunded: z.boolean().nullish(),
    })
    .nullish(),
  inStoreReturn: z.boolean().optional(),
  isPosReturn: z.boolean().default(false),
  pos: z.object({ locationId: z.string() }).nullish(),
  settlement: SettlementZodSchema.nullish(),
  instantRefund: z
    .object({
      astra_user_id: z.string(),
      astraRoutine: z.any().nullish(),
      astraRoutines: z.array(z.any()),
      astraRoutinePullback: z.any().nullish(),
      status: createEnum(InstantRefundStatus, {
        enumName: "InstantRefundStatus",
      }),
      sandbox: z.boolean(),
    })
    .nullish(),
  lastEmailReminder: z.date().nullish(),
  warnings: z.array(ReturnWarningZodSchema).optional(),
  advancedExchangeItems: z.array(ExchangeItemZodSchema).optional(),
  new_order_taxes: z.string().optional(),
  newOrderTaxesSet: PriceSetZodSchema.nullish(),
  newOrderTaxLineInfo: z
    .array(z.object({ title: z.string(), rate: z.number() }))
    .nullish(),
  discount_allocations: z.array(DiscountAllocationZodSchema).optional(),
  usePreDiscountPrice: z.boolean().optional(),
  // Changed from required to nullable - legacy data in MongoDB may have null expirationDate
  expirationDate: z.date().nullish(),
  filedWithCarrier: z.date().nullish(),

  some_processed: z.boolean().optional(),
  currentEmailFlows: z
    .array(
      z.object({
        emailFlowId: z.string(),
        currentStep: z.number(),
        continueDate: z.string(),
        fulfillmentId: z.string().optional(),
      }),
    )
    .optional(),
  testId: z.string().nullish(),
  treatmentId: z.string().nullish(),
  pickupVariant: z.string().nullish(),
  rejectReason: z.string().nullish(),
  processingInput: z
    .object({
      sendNotifications: z.boolean().nullish(),
      waiveDraftFee: z.boolean().nullish(),
      refundInstantExchangeRecoveryFee: z.boolean().nullish(),
      customerRecoveryAmount: z.number().nullish(),
      customerRejectionNotes: z.string().nullish(),
      customerImages: z.array(z.string()).nullish(),
      restockLocationId: z.string().nullish(),
      isProcessing: z.boolean().nullish(),
    })
    .nullish(),
  processingTriggers: z.array(ReturnProcessingTriggerSchema).nullish(),
  currency: createEnum(Currency, { enumName: "Currency" }).nullish(),
  presentmentCurrency: createEnum(Currency, { enumName: "Currency" }).nullish(),
  exchangeRate: z.number().default(1),
  newItemToExchangeRate: z.number().optional(),
  happyReturnsData: z
    .object({
      rmaId: z.string().nullish(),
      qrCode: z.string().nullish(),
      retailerId: z.string().nullish(),
    })
    .nullish(),
  thirdPartyRefund: z
    .object({ provider: z.enum(ThirdPartyRefundProvider) })
    .nullish(),
  provider: createEnum(Provider, { enumName: "Provider" }).nullish(),
  customerPortalVersion: createEnum(CustomerPortalVersion, {
    enumName: "CustomerPortalVersion",
  }).nullish(),
  externalIntegrationIds: z
    .array(
      z.object({
        provider: createEnum(ExternalIntegrationIdProviders, {
          enumName: "ExternalIntegrationIdProviders",
        }),
        type: createEnum(ExternalIntegrationIdTypes, {
          enumName: "ExternalIntegrationIdTypes",
        }),
        id: z.string(),
        shipmentGroupId: z.string().nullish(),
      }),
    )
    .nullish(),
  createExternalIntegrationIds: z
    .array(
      z.object({
        provider: createEnum(CreateExternalIntegrationIdProvider, {
          enumName: "CreateExternalIntegrationIdProvider",
        }),
        type: createEnum(CreateExternalIntegrationIdType, {
          enumName: "CreateExternalIntegrationIdType",
        }),
        id: z.string(),
        shipmentGroupId: z.string().nullish(),
      }),
    )
    .nullish(),
  agentReview: ReviewResultSchemaWithThreadId.partial().nullish(),
  agentReviewMetrics: AgentReviewMetricsSchema.nullish(),
  agentFraudReview: FraudAssessmentSchema.nullish(),
  agentFraudReviewMetrics: FraudDetectionInputSchema.nullish(),
  merchantFraudFeedback: z
    .object({ isFraud: z.boolean(), feedback: z.string().optional() })
    .nullish(),
  agentSummary: z.any().nullish(),
  returnChat: ReturnChatSchema.nullish(),
  exchangeAgentBillable: z.boolean().nullish(),
  isManualLabel: z.boolean().nullish(),
  manualLabelData: z
    .object({
      shipments: z
        .array(
          z.object({
            address: ReturnAddressSchema,
            returnItemIds: z.array(z.string()),
            packingSlipUrl: z.string().nullish(),
          }),
        )
        .nullish(),
    })
    .nullish(),
  approvedAt: z.date().nullish(),
  approvedBy: z.string().default(""),
  processedAt: z.date().nullish(),
  processedBy: z.string().default(""),
  upsellBillingTrigger: createEnum(UpsellBillingTrigger, {
    enumName: "UpsellBillingTrigger",
  }).nullish(),
  inmar: InmarZodSchema.nullish(),
  /**
   * @deprecated Replaced by `dropoffs.qrCodes`. Kept on the schema for the
   * BlueYonder webhook receiver and the trackable/email-reminder legacy
   * paths; do not add new readers.
   */
  blueYonder: BlueYonderZodSchema.nullish(),
  returnBear: ReturnBearZodSchema.nullish(),
  dropoffs: ReturnDropoffSchema.nullish(),
  reshop: ReshopZodSchema.nullish(),
  merchantCreated: z.boolean().nullish(),
  merchantCreatedByName: z.string().nullish(),
  giftReceiptId: zExt.objectId().nullish(),
  tnplContractId: zExt.objectId().nullish(),
  tnplCredit: z
    .object({
      amount: z.number(),
      currency: z.string(),
      coveredShipping: z.boolean().default(false),
      coveredShippingAmount: z.number().nullish(),
    })
    .nullish(),
  yofiReturnScores: z.record(z.string(), YofiScoreZodSchema).nullish(),
  reportedClaims: z
    .array(z.object({ claimId: z.string(), claimRetrievalCode: z.string() }))
    .nullish(),
  temporalWorkflowFailures: z
    .object({
      workflows: z.array(z.object({ name: z.string(), attempts: z.number() })),
      activities: z.array(z.object({ name: z.string(), attempts: z.number() })),
      merchantFriendlyErrorMessage: z.string().nullish(),
      technicalFriendlyErrorMessage: z.string().nullish(),
    })
    .nullish(),
  exchangeAgentSeen: z.boolean().nullish(),
  /**
   * @deprecated categorizationThreadId is now stored at the product level, not return level. Keep for backwards compatibility.
   */
  categorizationThreadId: zExt.objectId().nullish(),
  reimburseForFinalSale: z.boolean().nullish().default(true),
  createRmaResults: createRmaResultsArrayZodSchema,
  createExternalRmaResults: createExternalRmaResultsArrayZodSchema,
  createShipmentFailures: createShipmentFailuresArrayZodSchema,
  featureFlags: z.record(z.string(), z.boolean()).nullish(),
  refundOriginalShipping: z.boolean().default(false),
  refundOriginalShippingAmount: z.number().nullish(),
  sentWebhooksId: z.array(zExt.objectId()).nullish(),
  lastWebhookSent: z.date().nullish(),
  repair: z
    .object({
      invoiceId: z.string().nullish(),
      invoiceURL: z.string().nullish(),
      shopifyOrderId: z.string().nullish(),
    })
    .nullish(),
  invoiceStatus: createEnum(InvoiceStatus, {
    enumName: "InvoiceStatus",
  }).default(InvoiceStatus.PENDING),
  isGiftReturn: z.boolean().nullish(),
  returnTrackingTriggersTriggered: z
    .array(ReturnTrackingTriggerTriggeredSchema)
    .nullish(),
  source: createEnum(ReturnSource, { enumName: "ReturnSource" }).default(
    ReturnSource.REDO,
  ),
});

export const ReturnZodSchema: z.ZodType<ReturnZod> = ReturnZodSchema_;
export interface ReturnZod extends z.infer<typeof ReturnZodSchema_> {}

export interface Return extends ReturnZod {}

export enum WorkflowStatus {
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
}

export interface MerchantAppReturn
  extends z.infer<typeof MerchantAppReturnZodSchema_> {}

const StoredCreditWithCodeZodSchema = z.object({
  createdAt: z
    .union([z.date(), z.string()])
    .transform((val) => (val instanceof Date ? val.toISOString() : val)),
  updatedAt: z
    .union([z.date(), z.string()])
    .transform((val) => (val instanceof Date ? val.toISOString() : val)),
  priceRuleId: z.string().nullish(),
  discountCodeId: z.string().optional(),
  source: CreditSourceZodSchema.nullish(),
  products: z.array(zExt.objectId()).nullish(),
  value: z.number().nullish(),
  greenReturn: z.boolean(),
  code: z.string(),
});

const MerchantAppOriginalReturnOptionsZodSchema = z
  .object({
    advancedExchangeItems: z.array(MerchantAppExchangeItemZodSchema),
    new_order_taxes: z.string(),
    discount_allocations: z.array(DiscountAllocationZodSchema),
  })
  .optional();

/**
 * The merchant-server does some post-processing after retrieving the Return from the database
 * and adds more properties. This type represents the changes made.
 */
export const MerchantAppReturnZodSchema_ = ReturnZodSchema_.omit({
  storedCredits: true,
  advancedExchangeItems: true,
  originalReturnOptions: true,
}).extend({
  storedCredits: z.array(StoredCreditWithCodeZodSchema),
  advancedExchangeItems: z.array(MerchantAppExchangeItemZodSchema),
  originalReturnOptions: MerchantAppOriginalReturnOptionsZodSchema,

  exchanges: z.array(z.any()),
  draftOrder: z.any().optional(),
  customerOrders: z.array(z.any()), // todo need zod for order
  customerReturns: z.array(ReturnZodSchema),
  originOrders: MaybeOriginOrderArrayZodSchema.optional(),
  returnCreationStatus: z
    .enum([WorkflowStatus.IN_PROGRESS, WorkflowStatus.COMPLETED])
    .optional(),
  approvalCreationStatus: z
    .enum([WorkflowStatus.IN_PROGRESS, WorkflowStatus.COMPLETED])
    .optional(),
  triggerProcessingStatus: z
    .enum([WorkflowStatus.IN_PROGRESS, WorkflowStatus.COMPLETED])
    .optional(),
  triggerReadySideEffectsStatus: z
    .enum([WorkflowStatus.IN_PROGRESS, WorkflowStatus.COMPLETED])
    .optional(),
  loyaltyData: ReturnLoyaltyDataZodSchema,
  id: z.string(),
});

export const MerchantAppReturnZodSchema: z.ZodType<MerchantAppReturn> =
  MerchantAppReturnZodSchema_;
export interface MerchantAppReturn
  extends z.infer<typeof MerchantAppReturnZodSchema_> {}

export interface ShopifyVariant {
  sku: string;
  product_id: number;
}
export interface ShopifyVariantRest {
  id: number;
  variant_id: number;
  product_id: number;
  title: string;
  price: string;
  sku: string;
  position: number;
  inventory_policy: string;
  compare_at_price: string | null;
  created_at: string;
  updated_at: string;
  taxable: boolean;
  barcode: string | null;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  fulfillment_service: string;
  grams: number;
  inventory_management: string | null;
  requires_shipping: boolean;
  weight: number | null;
  weight_unit: string;
  inventory_item_id: number;
  inventory_quantity: number;
  old_inventory_quantity: number;
  admin_graphql_api_id: string;
  image_id: number | null;
}

export interface ImageRest {
  id: number;
  alt: string | null;
  position: number;
  product_id: number;
  created_at: string;
  updated_at: string;
  admin_graphql_api_id: string;
  width: number | null;
  height: number | null;
  src: string;
  variant_ids: number[];
}

export interface ShopifyProductRest {
  id: number;
  title: string;
  body_html: string;
  vendor: string;
  product_type: string;
  created_at: string;
  handle: string;
  updated_at: string;
  published_at: string;
  template_suffix: string;
  published_scope: string;
  tags: string;
  status: string;
  admin_graphql_api_id: string;
  variants: ShopifyVariantRest[] | undefined;
  options: {
    id: number;
    product_id: number;
    name: string;
    position: number;
    values: string[];
  }[];
  images: ImageRest[] | undefined;
  image: ImageRest | undefined;
}

export interface NewItem {
  isVariantExchange: boolean;
  images?: string[];
  price?: string;
  itemValue?: string;
  tax?: string;
  variantTitle?: string;
  variantId?: string;
  title?: string;
  shopifyVariant?: ShopifyVariant;
  quantity?: number;
  attributes?: Metadata[];
  outOfStock?: boolean;
  option1?: string;
  option2?: string;
  option3?: string;
}

export const RESTOCK_STRATEGY: { [key: string]: boolean } = {
  exchange: true,
  refund: true,
  store_credit: true,
  repair: false,
};

export interface LineItemAnswers {
  lineItemId: string;
  answers: MultipleChoiceAnswer[];
}

/**
 * Purchase value
 */
export function returnOriginalValue(return_: Return): number {
  return sumBy(return_.products || [], (product) => +product.price);
}

export function returnOriginalPrice(return_: Return): number {
  return sumBy(
    return_.products || [],
    (product) =>
      +(
        product.order.shopify.line_items.find(
          (lineItem: any) =>
            String(lineItem.id) === String(product.line_item_id),
        )?.price || product.price
      ),
  );
}

export function productTax(product: ProductZod): number {
  let taxValue = 0.0;
  const shopifyLineItem = product.order.shopify.line_items.find(
    (lineItem: any) => String(lineItem.id) === String(product.line_item_id),
  );
  const taxLines = shopifyLineItem?.tax_lines || [];
  for (const taxLine of taxLines) {
    taxValue += parseFloat(taxLine.price) / (shopifyLineItem?.quantity || 1);
  }

  return taxValue;
}

export function returnTax(return_: Return): number {
  let taxValue = 0.0;
  const products = return_.products || [];
  for (const product of products) {
    taxValue += productTax(product);
  }
  return parseFloat(taxValue.toFixed(2));
}

export function returnDiscounts(return_: Return): number {
  let discountValue = 0.0;
  const products = return_.products || [];
  for (const product of products) {
    const lineItem = product.order.shopify.line_items.find(
      (lineItem: any) => String(lineItem.id) === String(product.line_item_id),
    );
    const discountAllocations = lineItem?.discount_allocations;
    if (discountAllocations) {
      for (const allocation of discountAllocations) {
        discountValue += parseFloat(allocation.amount) / lineItem.quantity;
      }
    }
  }
  return parseFloat(discountValue.toFixed(2));
}

export function returnAdjustment(return_: Return): number {
  return sumBy(
    return_?.products || [],
    (product) => +product.price_adjustment! || 0,
  );
}

export function returnExchangeValue(return_: Return): number {
  return (
    sumBy(return_.products || [], (product) =>
      product.exchange_for ? +product.price || 0 : 0,
    ) +
    sumBy(
      return_.advancedExchangeItems || [],
      (product) => +(product.price || "0"),
    )
  );
}

export interface Returner {
  firstName?: string | null;
  lastName?: string | null;
  name: string;
  email: string;
  phone?: string | null;
}

// If it's gift return, the returner's info will be in the shipping address
// and the original customer's info will be in the customer object, so favor
// shipping address info first.
export function getReturner(return_: {
  shipping_address?: Omit<ReturnAddress, "location"> | null;
  customer?: CustomerSummary | null;
}): Returner {
  const firstName =
    return_.shipping_address?.first_name || return_.customer?.first_name;
  const lastName =
    return_.shipping_address?.last_name || return_.customer?.last_name;
  const name = return_.shipping_address?.name || `${firstName} ${lastName}`;
  return {
    firstName,
    lastName,
    name,
    email:
      return_.shipping_address?.email ||
      return_.customer?.email ||
      "Unknown email",
    phone: return_.shipping_address?.phone || return_.customer?.phone_number,
  };
}

export function getBuyer(return_: {
  shipping_address?: Omit<ReturnAddress, "location"> | null;
  customer?: CustomerSummary | null;
}) {
  const firstName = return_.customer?.first_name;
  const lastName = return_.customer?.last_name;
  const name = `${firstName} ${lastName}`;
  return {
    firstName,
    lastName,
    name,
    email: return_.customer?.email,
    phone: return_.customer?.phone_number,
  };
}

export function isGiftReturn(return_: {
  shipping_address?: Omit<ReturnAddress, "location"> | null;
  customer?: CustomerSummary | null;
  isGiftReturn?: boolean | null;
}): boolean {
  // Use the isGiftReturn field if it's set (true or false)
  if (return_.isGiftReturn != null) {
    return return_.isGiftReturn;
  }

  // Fallback to the old logic for backwards compatibility
  const returner = getReturner(return_);
  const buyer = getBuyer(return_);
  if (isDefined(buyer.email)) {
    return returner.email.toLowerCase() !== buyer.email.toLowerCase();
  }
  return returner.phone !== buyer.phone;
}

const EXCLUDED_SALES_CHANNELS: ChannelId[] = ["matrixify", "corso", "facebook"];

export function isExcludedOrder(order: {
  shopify: { source_name: string };
}): boolean {
  const excludedSourceNames = EXCLUDED_SALES_CHANNELS.flatMap(
    (channelId) => channelIdToSalesChannel[channelId].sourceNames,
  );
  return excludedSourceNames.some((sourceName) =>
    order.shopify.source_name.includes(sourceName),
  );
}

export function allProductsHaveDispositionCompleted(
  returnProducts: {
    merchant_grade?: string | null | undefined;
    merchant_outcome?: string | null | undefined;
  }[],
): boolean {
  return returnProducts.every(
    (product) => !!product.merchant_grade && !!product.merchant_outcome,
  );
}

export function canRestockItemsManually(orderProvider: Provider): boolean {
  return orderProvider === Provider.SHOPIFY;
}

export const allProductsAreRepair = (products?: ProductZod[]) => {
  return !!products?.every((product) => product.strategy === "repair");
};

export const isGreenReturnOnly = (products: { green_return?: boolean }[]) => {
  return (
    products.length > 0 && products.every((product) => product.green_return)
  );
};

/**
 * Whether a product should count toward return label weight. Non-physical items
 * (requiresShipping === false) are excluded; undefined/null is treated as
 * physical so weight is never dropped for items lacking the flag.
 */
export const productRequiresShipping = (
  product?: { requiresShipping?: boolean | null } | null,
): boolean => product?.requiresShipping !== false;

export interface EssentialInnerRefundSchema {
  id: number | string;
  refund_line_items: {
    line_item_id: number;
    subtotal: number;
    total_tax: number;
    subtotal_set: { presentment_money: { currency_code: string } };
  }[];
  transactions: { amount: string; currency: string; created_at: string }[];
}
/*
  Because nothing was typed when we use the REST API from shopify, we need to have some source of truth
  of what the refund looks like.
 */
export interface EssentialRefundSchema {
  refund: EssentialInnerRefundSchema;
}

export const MERCHANT_CARRIER_RETURN_LABELS_LOG_STAMP =
  "merchantCarrierReturnLabels";

export function getVariantOfProduct(
  product: ShopifyProductRest,
  variantId: string,
): ShopifyVariantRest | undefined {
  return product.variants?.find(
    (variant: ShopifyVariantRest) => variant.id.toString() === variantId,
  );
}

export { normalizePostalCodeForSearch as normalizeOrderZipCode } from "@redotech/util/postal-code";

export { ReturnBearCaseMethodType } from "@redotech/returnbear-model/shared/common-enums";
